import { useEffect, useRef } from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import { jwtDecode } from 'jwt-decode';

import { authChannelService, authDBService } from '~/services';
import { useUserInfoStore } from '~/store';
import { type TypeDecodedToken } from '~/utils';

import { AUTH_CHANNEL_MESSAGE, type TypeAuthChannelMessage } from '~/constants';

/**
 * IndexedDB의 access_token을 부작용 없이 디코딩한다. 토큰이 없거나 디코딩에 실패하면 null을 반환한다.
 * (getDecodedJwtValue는 실패 시 얼럿/로그아웃 리다이렉트를 일으키므로 동기화 경로에서는 사용하지 않는다.)
 */
const decodeStoredToken = async (): Promise<TypeDecodedToken | null> => {
  try {
    const loginInfo = await authDBService.getLoginInfo();
    const token = loginInfo?.access_token;

    if (!token) {
      return null;
    }

    return jwtDecode<TypeDecodedToken>(token);
  } catch {
    return null;
  }
};

/**
 * 다른 탭/창에서 로그인이 발생했을 때 현재 탭의 계정 정보와 실제 토큰의 불일치를 감지하는 훅.
 *
 * IndexedDB(access_token)는 탭 간 공유되어 최신 로그인으로 덮어써지지만,
 * 화면에 표시되는 계정 정보는 부팅 시 세팅된 값 그대로 남는다.
 * 다른 탭의 로그인 신호를 받아 최신 토큰을 디코딩하고, 현재 표시 중인 계정과 다르면 얼럿을 띄운 뒤 최신 계정 정보로 갱신한다.
 */
const useAuthSync = () => {
  const isDialogOpenRef = useRef(false);

  useEffect(() => {
    const handleMessage = async (message: TypeAuthChannelMessage) => {
      // 이미 갱신 얼럿이 떠 있으면 중복으로 띄우지 않는다.
      if (message !== AUTH_CHANNEL_MESSAGE.LOGIN || isDialogOpenRef.current) {
        return;
      }

      const decodedToken = await decodeStoredToken();

      if (!decodedToken) {
        return;
      }

      const currentLoginId = useUserInfoStore.getState().userInfo?.loginId;

      // 로그인하지 않은 화면(currentLoginId 없음)에서는 갱신할 계정 정보가 없으므로 무시한다.
      if (!currentLoginId) {
        return;
      }

      // 계정(loginId)이 같으면 같은 계정이므로 무시한다.
      if (decodedToken.loginId === currentLoginId) {
        return;
      }

      isDialogOpenRef.current = true;

      dialogService.addDialog({
        title: '로그인 정보 변경',
        children: (
          <>
            동일 브라우저에서 다른 계정이 로그인되었습니다.
            <br />
            확인 선택 시 새 로그인 정보로 갱신되며, 작성&nbsp;중이던 내용은 저장되지 않습니다.
          </>
        ),
        onConfirm: () => {
          isDialogOpenRef.current = false;
          window.location.reload();
        },
      });
    };

    const unsubscribe = authChannelService.subscribe(handleMessage);

    return unsubscribe;
  }, []);
};

export default useAuthSync;
